import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Paper,
  Divider
} from '@mui/material';

// Import icons
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import CodeIcon from '@mui/icons-material/Code';
import WebIcon from '@mui/icons-material/Web';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import SortIcon from '@mui/icons-material/Sort';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import AppsIcon from '@mui/icons-material/Apps';
import SecurityIcon from '@mui/icons-material/Security';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DataObjectIcon from '@mui/icons-material/DataObject';

const ApplicationsTab: React.FC = () => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Introduction Section - Consistent with other tabs */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: 'primary.lighter', borderLeft: '5px solid', borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <AppsIcon sx={{ fontSize: '2.5rem', mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
            Applications
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          The <strong>Applications section</strong> provides comprehensive management and exploration capabilities 
          for all your Microsoft Entra ID applications. Browse, search, filter, and analyze your application 
          portfolio with powerful tools designed for administrators and developers.
        </Typography>
        <Typography variant="body2" color="text.primary">
          Discover applications across your tenant, understand their configurations, manage permissions, 
          and seamlessly transition to testing with integrated authentication flows and token analysis tools.
        </Typography>
      </Paper>

      {/* Applications Overview Section */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 3, mb: 2, borderBottom: '2px solid', borderColor: 'primary.light', pb: 1 }}>
        Key Capabilities
      </Typography>
      
      {/* Main Overview Card */}
      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          {/* Enhanced Feature Grid */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 2,
                bgcolor: 'rgba(33, 150, 243, 0.02)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <ManageSearchIcon sx={{ 
                  fontSize: 48, 
                  mb: 2, 
                  color: '#1976d2',
                  p: 1,
                  bgcolor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: '50%'
                }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Advanced Search & Discovery
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Use powerful search capabilities with intelligent filtering across names, IDs, URIs, and application properties.
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                  <Chip size="small" label="Smart Search" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Regex Support" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 2,
                bgcolor: 'rgba(76, 175, 80, 0.02)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <DataObjectIcon sx={{ 
                  fontSize: 48, 
                  mb: 2, 
                  color: '#388e3c',
                  p: 1,
                  bgcolor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '50%'
                }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Detailed Information
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Access comprehensive application details including configurations, permissions, and metadata.
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                  <Chip size="small" label="Full Metadata" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Ownership Info" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 2,
                bgcolor: 'rgba(156, 39, 176, 0.02)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <VpnKeyIcon sx={{ 
                  fontSize: 48, 
                  mb: 2, 
                  color: '#7b1fa2',
                  p: 1,
                  bgcolor: 'rgba(156, 39, 176, 0.1)',
                  borderRadius: '50%'
                }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Authentication Testing
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Select applications for immediate authentication testing and token analysis.
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                  <Chip size="small" label="One-Click Testing" sx={{ bgcolor: '#fce4ec', color: '#ad1457' }} />
                  <Chip size="small" label="Token Analysis" sx={{ bgcolor: '#fce4ec', color: '#ad1457' }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Search and Filtering Features */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SearchIcon sx={{ mr: 1, color: '#1976d2' }} />
                <Typography variant="h6">Search Capabilities</Typography>
              </Box>
              
              <Typography variant="body2" paragraph color="text.secondary">
                AppConfig² provides intelligent search functionality that understands different application attributes and patterns.
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary="Intelligent Text Search"
                    secondary="Search across application names, descriptions, and IDs with fuzzy matching"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary="Property-Based Search"
                    secondary="Find applications by redirect URIs, scopes, and configuration properties"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary="Type Shortcuts"
                    secondary="Use shortcuts like 'spa', 'web', 'api', 'owned', 'multi-tenant' for quick filtering"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary="Relevance Ranking"
                    secondary="Results automatically ranked by relevance with match highlighting"
                  />
                </ListItem>
              </List>

              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Search Examples
                </Typography>
                <Typography variant="body2">
                  Try searching for: "owned", "multi-tenant", "localhost", or specific app names
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TuneIcon sx={{ mr: 1, color: '#388e3c' }} />
                <Typography variant="h6">Filtering & Organization</Typography>
              </Box>
              
              <Typography variant="body2" paragraph color="text.secondary">
                Organize and filter your applications using multiple criteria and smart categorization.
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary="Application Type Filtering"
                    secondary="Filter by SPA, Web, API, or hybrid application types"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary="Ownership-Based Views"
                    secondary="View only applications you own or have administrative access to"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary="Pagination & Sorting"
                    secondary="Efficient pagination with customizable page sizes and sorting options"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary="Visual Indicators"
                    secondary="Quick identification of SAML apps, multi-tenant apps, and special configurations"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Application Types Breakdown */}
      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Application Types & Features</Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <CodeIcon sx={{ 
                  fontSize: 40, 
                  mb: 1, 
                  color: '#1976d2',
                  p: 1,
                  bgcolor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: 1
                }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Single-Page Apps (SPA)
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  JavaScript applications running in browsers with PKCE authentication flow.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                  <Chip size="small" label="PKCE" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Public Client" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <WebIcon sx={{ 
                  fontSize: 40, 
                  mb: 1, 
                  color: '#388e3c',
                  p: 1,
                  bgcolor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: 1
                }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Web Applications
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Server-side applications with confidential client capabilities and secrets.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                  <Chip size="small" label="Confidential" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Server-Side" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <SettingsEthernetIcon sx={{ 
                  fontSize: 40, 
                  mb: 1, 
                  color: '#f57c00',
                  p: 1,
                  bgcolor: 'rgba(255, 152, 0, 0.1)',
                  borderRadius: 1
                }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  APIs & Services
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Backend APIs exposing scopes and permissions for resource access.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                  <Chip size="small" label="OAuth Scopes" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                  <Chip size="small" label="Resource Server" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <SecurityIcon sx={{ 
                  fontSize: 40, 
                  mb: 1, 
                  color: '#7b1fa2',
                  p: 1,
                  bgcolor: 'rgba(156, 39, 176, 0.1)',
                  borderRadius: 1
                }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  SAML Applications
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Legacy and enterprise applications using SAML 2.0 authentication.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                  <Chip size="small" label="SAML 2.0" sx={{ bgcolor: '#fce4ec', color: '#ad1457' }} />
                  <Chip size="small" label="Enterprise" sx={{ bgcolor: '#fce4ec', color: '#ad1457' }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Key Features Section */}
      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Key Features & Capabilities</Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon><FilterListIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Smart Application Filtering"
                    secondary="Filter by application type, ownership, tenant configuration, and authentication method"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><ManageSearchIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Advanced Search Functionality"
                    secondary="Search by name, ID, URIs, scopes, and other properties with intelligent ranking"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><VisibilityIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Comprehensive Application Details"
                    secondary="View detailed information including permissions, redirect URIs, secrets, and certificates"
                  />
                </ListItem>
              </List>
            </Grid>

            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon><PersonIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Ownership & Access Management"
                    secondary="Track application ownership and quickly identify apps you can manage"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><SortIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Flexible Sorting & Pagination"
                    secondary="Sort by name, type, or tenant configuration with customizable page sizes"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><VpnKeyIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Direct Authentication Testing"
                    secondary="Select any application for immediate authentication testing and token analysis"
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Usage Tips */}
      <Card sx={{ borderRadius: 2, bgcolor: '#f8f9fc' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Usage Tips & Best Practices</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                  Efficient Search
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Use type shortcuts"
                      secondary="Type 'spa', 'web', 'api', or 'owned' for quick filtering"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Search by partial matches"
                      secondary="Enter partial app names or IDs for fuzzy matching"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#388e3c' }}>
                  Organization
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Use 'My Apps' filter"
                      secondary="Quickly find applications you own or manage"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Sort strategically"
                      secondary="Sort by name for browsing, by type for architecture review"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7b1fa2' }}>
                  Testing Workflow
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="One-click testing"
                      secondary="Select an app to immediately test its authentication flow"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Review configurations"
                      secondary="Check redirect URIs and permissions before testing"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Information Note */}
      <Paper elevation={0} sx={{ p: 3, mt: 4, backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <InfoOutlinedIcon color="info" sx={{ mr: 1 }} />
          <Typography variant="h6" color="info.dark" sx={{ fontWeight: 500 }}>
            Getting Started with Applications
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Start by exploring your tenant's application portfolio using the search and filtering capabilities. 
          When you find an application of interest, select it to access detailed configuration information 
          and seamlessly transition to authentication testing with the integrated App Tester tools.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ApplicationsTab;